import { WEB_STUDIO_CLOUD_API_OPERATION_CONTRACT_VERSION } from "./generatedClient";

export const WEB_STUDIO_PUBLIC_BOOT_CONFIG_ENV_ALLOWLIST = [
  "VITE_JACQOS_WEB_STUDIO_BUILD_ID",
  "VITE_JACQOS_WEB_STUDIO_BUILT_AT",
  "VITE_JACQOS_WEB_STUDIO_TARGET_ENVIRONMENT",
  "VITE_JACQOS_WEB_STUDIO_ORIGIN",
  "VITE_JACQOS_WEB_STUDIO_ASSET_BASE",
  "VITE_JACQOS_WEB_STUDIO_BOOT_ENDPOINT",
  "VITE_JACQOS_WEB_STUDIO_RETENTION_POLICY",
] as const;

const WEB_STUDIO_PUBLIC_BOOT_CONFIG_ENV_ALLOWLIST_SET = new Set<string>(
  WEB_STUDIO_PUBLIC_BOOT_CONFIG_ENV_ALLOWLIST,
);

const SENSITIVE_ENV_NAME = /(SECRET|PASSWORD|PRIVATE|BEARER|TOKEN|API_KEY|SIGNING_KEY)/i;

export interface WebStudioPublicBootConfig {
  buildId: string;
  builtAt: string;
  targetEnvironment: string;
  origin: string;
  assetBase: string;
  bootEndpoint: string;
  retentionPolicy: string;
  redactionState: "public_metadata_only";
  publicEnvAllowlist: readonly string[];
}

export interface WebStudioCachedStateInput {
  offline: boolean;
  stale: boolean;
  contractVersion: string;
  expectedContractVersion?: string;
  authorizationState?: string;
  /** Primary backend diagnostic code reported alongside the boot snapshot. */
  diagnosticCode?: string;
}

export interface WebStudioCachedStateClassification {
  mode: "fresh" | "read_only_cache" | "unauthenticated" | "blocked";
  allowProtectedReads: boolean;
  allowMutations: boolean;
  diagnosticCode?: string;
}

/** Typed prefix reserved for backend auth-shaped diagnostics. */
export const WEB_STUDIO_AUTH_DIAGNOSTIC_PREFIX = "web_studio.auth.";

/**
 * Backend authorization states that mean the account session itself is
 * absent (signed out, expired, or revoked). Narrower scope-loss states such
 * as `unauthorized_scope`, `forbidden`, `not_found`, or `redacted` are NOT
 * account-session loss and keep their blocked classification.
 */
const ACCOUNT_SESSION_LOSS_AUTHORIZATION_STATES: ReadonlySet<string> = new Set([
  "signed_out",
  "reauth_required",
  "revoked",
]);

/**
 * Operation-level typed diagnostic codes that report the account session was
 * lost mid-use. Scope-loss codes (for example
 * `web_studio.auth.wrong_org_or_project_scope` or `web_studio.auth.forbidden`)
 * are deliberately excluded: they render in place, never as the sign-in gate.
 */
export const WEB_STUDIO_AUTH_SESSION_LOSS_DIAGNOSTIC_CODES: ReadonlySet<string> = new Set([
  "web_studio.auth.signed_out",
  "web_studio.auth.expired",
  "web_studio.auth.revoked",
  // Live-facade spellings: protected operations report session loss as
  // `web_studio.auth.{ManagementAuthzError::code()}`.
  "web_studio.auth.management_session_not_found",
  "web_studio.auth.management_session_not_authenticated",
  "web_studio.auth.management_session_expired",
  "web_studio.auth.management_session_revoked",
]);

export function isWebStudioAuthSessionLossDiagnostic(code: string | undefined | null): boolean {
  return code !== undefined && code !== null
    ? WEB_STUDIO_AUTH_SESSION_LOSS_DIAGNOSTIC_CODES.has(code)
    : false;
}

export function readWebStudioPublicBootConfig(
  env: Record<string, string | boolean | undefined> = import.meta.env,
): WebStudioPublicBootConfig {
  const unsupportedPublicKeys = Object.keys(env)
    .filter((key) => key.startsWith("VITE_JACQOS_WEB_STUDIO_"))
    .filter((key) => !WEB_STUDIO_PUBLIC_BOOT_CONFIG_ENV_ALLOWLIST_SET.has(key));
  if (unsupportedPublicKeys.length > 0) {
    throw new Error(
      `Unsupported Web Studio public boot config keys: ${unsupportedPublicKeys.sort().join(", ")}`,
    );
  }

  const sensitivePublicKeys = Object.keys(env)
    .filter((key) => key.startsWith("VITE_"))
    .filter((key) => SENSITIVE_ENV_NAME.test(key));
  if (sensitivePublicKeys.length > 0) {
    throw new Error(
      `Sensitive-looking Web Studio browser config is forbidden: ${sensitivePublicKeys.sort().join(", ")}`,
    );
  }

  const config: WebStudioPublicBootConfig = {
    buildId: stringEnv(env, "VITE_JACQOS_WEB_STUDIO_BUILD_ID", "local-dev"),
    builtAt: stringEnv(env, "VITE_JACQOS_WEB_STUDIO_BUILT_AT", "local"),
    targetEnvironment: stringEnv(env, "VITE_JACQOS_WEB_STUDIO_TARGET_ENVIRONMENT", "local"),
    origin: stringEnv(env, "VITE_JACQOS_WEB_STUDIO_ORIGIN", "same-origin-management"),
    assetBase: stringEnv(env, "VITE_JACQOS_WEB_STUDIO_ASSET_BASE", "/"),
    bootEndpoint: stringEnv(env, "VITE_JACQOS_WEB_STUDIO_BOOT_ENDPOINT", "/v1/web-studio/boot"),
    retentionPolicy: stringEnv(
      env,
      "VITE_JACQOS_WEB_STUDIO_RETENTION_POLICY",
      "local-build-output-not-retained",
    ),
    redactionState: "public_metadata_only",
    publicEnvAllowlist: WEB_STUDIO_PUBLIC_BOOT_CONFIG_ENV_ALLOWLIST,
  };
  assertWebStudioBootEndpointAllowed(config);
  return config;
}

export function assertWebStudioBootEndpointAllowed(config: WebStudioPublicBootConfig): void {
  if (config.targetEnvironment === "local") {
    return;
  }
  if (config.bootEndpoint.startsWith("/")) {
    return;
  }
  const endpoint = new URL(config.bootEndpoint);
  if (endpoint.protocol !== "https:") {
    throw new Error(`Web Studio production boot endpoint must use HTTPS: ${config.bootEndpoint}`);
  }
  if (isUnsafeBootHost(endpoint.hostname)) {
    throw new Error(
      `Web Studio production boot endpoint targets an unsafe origin: ${endpoint.origin}`,
    );
  }
  const approvedOrigin =
    config.origin === "same-origin-management" ? undefined : new URL(config.origin).origin;
  if (approvedOrigin && endpoint.origin !== approvedOrigin) {
    throw new Error(
      `Web Studio production boot endpoint ${endpoint.origin} is not the approved origin ${approvedOrigin}`,
    );
  }
}

function isUnsafeBootHost(hostname: string): boolean {
  const host = hostname.toLowerCase().replace(/^\[|\]$/g, "");
  if (
    host === "localhost" ||
    host === "::1" ||
    host === "0.0.0.0" ||
    host.endsWith(".local") ||
    host === "169.254.169.254"
  ) {
    return true;
  }
  if (host.startsWith("fc") || host.startsWith("fd") || host.startsWith("fe80")) {
    return true;
  }
  const octets = host.split(".").map((part) => Number(part));
  if (octets.length !== 4 || octets.some((octet) => !Number.isInteger(octet))) {
    return false;
  }
  const [first, second] = octets;
  if (first === undefined || second === undefined) {
    return false;
  }
  return (
    first === 10 ||
    first === 127 ||
    (first === 172 && second >= 16 && second <= 31) ||
    (first === 192 && second === 168) ||
    (first === 169 && second === 254)
  );
}

export function classifyWebStudioCachedState(
  input: WebStudioCachedStateInput,
): WebStudioCachedStateClassification {
  const expectedContractVersion =
    input.expectedContractVersion ?? WEB_STUDIO_CLOUD_API_OPERATION_CONTRACT_VERSION;
  if (input.contractVersion !== expectedContractVersion) {
    return {
      mode: "blocked",
      allowProtectedReads: false,
      allowMutations: false,
      diagnosticCode: "web_studio.cache.contract_mismatch",
    };
  }
  if (input.authorizationState !== "authorized") {
    // Auth-shaped classification is keyed exclusively on the backend's typed
    // authorization state plus a `web_studio.auth.*` diagnostic code: only an
    // absent account session (signed out / expired / revoked) renders the
    // sign-in gate. Everything else — contract mismatch (above), scope loss,
    // origin or availability failures — stays blocked with its own typed
    // diagnostic rendering.
    if (
      input.authorizationState !== undefined &&
      ACCOUNT_SESSION_LOSS_AUTHORIZATION_STATES.has(input.authorizationState) &&
      input.diagnosticCode?.startsWith(WEB_STUDIO_AUTH_DIAGNOSTIC_PREFIX) === true
    ) {
      return {
        mode: "unauthenticated",
        allowProtectedReads: false,
        allowMutations: false,
        diagnosticCode: input.diagnosticCode,
      };
    }
    return {
      mode: "blocked",
      allowProtectedReads: false,
      allowMutations: false,
      diagnosticCode: "web_studio.cache.authorization_required",
    };
  }
  if (input.offline || input.stale) {
    return {
      mode: "read_only_cache",
      allowProtectedReads: true,
      allowMutations: false,
      diagnosticCode: input.offline
        ? "web_studio.cache.offline_read_only"
        : "web_studio.cache.stale_read_only",
    };
  }
  return {
    mode: "fresh",
    allowProtectedReads: true,
    allowMutations: true,
  };
}

function stringEnv(
  env: Record<string, string | boolean | undefined>,
  key: string,
  fallback: string,
): string {
  const value = env[key];
  return typeof value === "string" && value.trim() ? value.trim() : fallback;
}
